import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";

type Tab = "herramienta" | "evidencia";

interface PhotoViewerModalProps {
  open: boolean;
  onClose: () => void;
  /** Fotos de la herramienta (tab "Herramienta nueva") */
  toolPhotos: string[];
  /** Fotos de evidencia (tab "Evidencia reciente") */
  evidencePhotos: string[];
  /** Tab e índice con los que abre */
  initialTab?: Tab;
  initialIndex?: number;
}

/**
 * Modal reutilizable para ver en grande las fotos de una herramienta.
 * Dos tabs: "Herramienta nueva" (fotos de la herramienta) y "Evidencia
 * reciente" (fotos de evidencia). Miniaturas a la izquierda + imagen grande
 * con flechas. Si un tab no tiene fotos, muestra un mensaje.
 */
export default function PhotoViewerModal({
  open,
  onClose,
  toolPhotos,
  evidencePhotos,
  initialTab = "herramienta",
  initialIndex = 0,
}: PhotoViewerModalProps) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setIndex(initialIndex);
    }
  }, [open, initialTab, initialIndex]);

  if (!open) return null;

  const photos = tab === "herramienta" ? toolPhotos : evidencePhotos;
  const emptyMsg =
    tab === "herramienta"
      ? "Sin fotos de la herramienta"
      : "Sin fotos de evidencia";
  const safeIndex = Math.min(index, Math.max(0, photos.length - 1));
  const current = photos[safeIndex];

  const switchTab = (t: Tab) => {
    setTab(t);
    setIndex(0);
  };
  const prev = () =>
    setIndex((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIndex((i) => (i + 1) % photos.length);

  const TabBtn = ({ value, label }: { value: Tab; label: string }) => (
    <button
      type="button"
      onClick={() => switchTab(value)}
      className={`py-2 px-2 text-sm font-semibold border-b-2 transition-colors ${
        tab === value
          ? "border-[#477FFA] text-[#477FFA]"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {label}
    </button>
  );

  return (
    <CapaDeModal zIndex={60} onClickFondo={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cerrar */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-lg font-bold text-gray-900 mb-3">Ver fotos</h3>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 mb-4">
          <TabBtn value="herramienta" label="Fotografías" />
          <TabBtn value="evidencia" label="Última evidencia" />
        </div>

        {photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <ImageOff className="w-10 h-10 mb-2 text-gray-300" />
            <p className="text-sm">{emptyMsg}</p>
          </div>
        ) : (
          <div className="flex gap-4">
            {/* Miniaturas */}
            <div className="flex flex-col gap-3">
              {photos.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                    i === safeIndex ? "border-[#477FFA]" : "border-gray-200"
                  }`}
                >
                  <img
                    src={src}
                    alt={`Miniatura ${i + 1}`}
                    className="w-full h-full object-contain bg-white"
                  />
                </button>
              ))}
            </div>

            {/* Imagen grande + flechas */}
            <div
              className="relative flex-1 rounded-xl border border-gray-200 bg-white flex items-center justify-center"
              style={{ minHeight: 380 }}
            >
              {photos.length > 1 && (
                <button
                  type="button"
                  onClick={prev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <img
                src={current}
                alt="Foto"
                className="max-h-[420px] max-w-full object-contain p-4"
              />
              {photos.length > 1 && (
                <button
                  type="button"
                  onClick={next}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </CapaDeModal>
  );
}
